import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Card,
  CrudMode,
  DataDisplay,
  formatBoolean,
  formatSchoolYear,
  GeneralValidationErrors,
  Grid,
  GridBreak,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { StudentGrade, StudentGradeEditResource, StudentGradeViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";
import ApiService from "services/apiService";

export const StudentGradePageBody = (props: {
  crudMode: CrudMode;
  grade: StudentGrade;
  setGrade: (value: StudentGrade) => void;
  viewResource: StudentGradeViewResource;
  editResource: StudentGradeEditResource;
  validationErrors: ValidationErrors;
}) => {
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const generationValidationErrors = props.validationErrors[pn<StudentGrade>("generalValidationErrors")];

  const termCount = props.viewResource.termTypes?.find((x) => x.id === props.grade.termTypeId)?.termCount || 0;

  const yearField = () => {
    const propertyName = pn<StudentGrade>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.grade.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.grade.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGrade(
            produce(props.grade, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const termTypeField = () => {
    const propertyName = pn<StudentGrade>("termTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.grade.termType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.termTypes?.find((x) => x.id === props.grade.termTypeId)}
        options={props.viewResource.termTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGrade(
            produce(props.grade, (draft) => {
              draft.termTypeId = value?.id;
              draft.termNumber = undefined;
            })
          )
        }
      />
    );
  };

  const termNumberField = () => {
    const propertyName = pn<StudentGrade>("termNumber");
    const property = properties.find((x) => x.propertyName === propertyName);
    const termNumbers = Array.from({ length: termCount }, (_, i) => i + 1);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.grade.termNumber}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={termNumbers.find((x) => x === props.grade.termNumber)}
        options={termNumbers}
        getOptionLabel={(option) => option.toString()}
        onChange={(e, value) =>
          props.setGrade(
            produce(props.grade, (draft) => {
              draft.termNumber = value;
            })
          )
        }
      />
    );
  };

  const classField = () => {
    const propertyName = pn<StudentGrade>("classId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.grade.class?.name}</DataDisplay>
    ) : (
      <Stack direction="column" spacing="sm">
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.grade.class}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setGrade(
              produce(props.grade, (draft) => {
                draft.classId = value?.id;
                draft.class = value;
              })
            )
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          fetchFunction={(searchString: string) => ApiService.search.classes(searchString, props.grade.schoolId)}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Stack direction="column">
                <Stack direction="row" spacing="xs" alignItems="center">
                  <Box>{option.name}</Box>
                  {option.identification && <Typography variant="caption">({option.identification})</Typography>}
                </Stack>
                <Typography variant="caption">{option.school?.name}</Typography>
              </Stack>
            </Box>
          )}
        />

        <Select
          label="Limit classes by School"
          value={props.editResource.schools?.find((x) => x.id === props.grade.schoolId)}
          options={props.editResource.schools}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setGrade(
              produce(props.grade, (draft) => {
                draft.schoolId = value?.id;
              })
            )
          }
          size="small"
        />
      </Stack>
    );
  };

  const gradeField = () => {
    const propertyName = pn<StudentGrade>("gradeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.grade.grade?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.grades?.find((x) => x.id === props.grade.gradeId)}
        options={props.editResource.grades}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGrade(
            produce(props.grade, (draft) => {
              draft.gradeId = value?.id;
            })
          )
        }
      />
    );
  };

  const reasonMissingField = () => {
    const propertyName = pn<StudentGrade>("reasonMissingId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.grade.reasonMissing?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.reasonMissings?.find((x) => x.id === props.grade.reasonMissingId)}
        options={props.editResource.reasonMissings}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGrade(
            produce(props.grade, (draft) => {
              draft.reasonMissingId = value?.id;
            })
          )
        }
      />
    );
  };

  const finalGradeField = () => {
    const propertyName = pn<StudentGrade>("finalGrade");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.grade.finalGrade)}</DataDisplay>
    ) : (
      <Select
        label={property?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.grade.finalGrade)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGrade(
            produce(props.grade, (draft) => {
              draft.finalGrade = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      {generationValidationErrors && (
        <Grid item xs={fullFieldBreakpoints}>
          <GeneralValidationErrors errors={generationValidationErrors} />
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {termTypeField()}
      </Grid>

      {termCount > 0 && (
        <Grid item xs={smallFieldBreakpoints}>
          {termNumberField()}
        </Grid>
      )}

      <GridBreak />

      <Grid item xs={smallFieldBreakpoints}>
        {classField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {gradeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {finalGradeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {reasonMissingField()}
      </Grid>
    </Grid>
  );
};
